import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Tv } from 'lucide-react'
import api from '../api/client'

interface SeasonInfo {
  season_number: number
  episode_count: number
}

interface Series {
  id: string
  title: string
  year: number | null
  overview: string | null
  poster_path: string | null
  library_id: string
  episode_count: number
  seasons: SeasonInfo[]
}

export default function TvShows() {
  const { data: seriesList, isLoading } = useQuery<Series[]>({
    queryKey: ['series'],
    queryFn: async () => {
      const response = await api.get('/series')
      return response.data
    },
  })

  return (
    <div className="p-4 sm:p-6">
      <h1 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">TV Shows</h1>

      {isLoading ? (
        <div className="text-dark-400">Loading...</div>
      ) : seriesList && seriesList.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-6">
          {seriesList.map((series) => (
            <Link
              key={series.id}
              to={`/tv/${series.id}`}
              className="card group hover:bg-dark-700 transition-colors cursor-pointer"
            >
              <div className="aspect-[2/3] mb-4 bg-dark-700 rounded-lg flex items-center justify-center overflow-hidden relative">
                {series.poster_path ? (
                  <img
                    src={series.poster_path}
                    alt={series.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Tv size={48} className="text-dark-500" />
                )}

                {/* New indicator - could indicate unwatched episodes */}
                <div className="absolute top-0 right-0 w-0 h-0 border-t-[28px] border-t-primary-500 border-l-[28px] border-l-transparent" />
              </div>
              <h3 className="font-medium truncate group-hover:text-primary-400">
                {series.title}
              </h3>
              <div className="text-sm text-dark-400 space-y-1">
                <p>{series.seasons.length} Season{series.seasons.length !== 1 ? 's' : ''}</p>
                <p>{series.episode_count} Episode{series.episode_count !== 1 ? 's' : ''}</p>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="card text-center py-12">
          <Tv size={48} className="text-dark-500 mx-auto mb-4" />
          <p className="text-dark-400">No TV shows found</p>
          <p className="text-dark-500 text-sm mt-2">
            Add a TV library and scan it to see your shows here
          </p>
        </div>
      )}
    </div>
  )
}
